import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Missing reset token"); return; }
    if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset — sign in with your new password");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center mx-auto">
            <img src="/logo-mark.png" alt="Arrivo" className="w-14 h-14 object-contain" />
          </div>
          <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
            ARRIVO
          </h1>
          <p className="text-sm text-muted-foreground">Set a new password</p>
        </div>

        {!token ? (
          <div className="space-y-4 text-center" data-testid="reset-missing-token">
            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-red-400">
              This link is invalid or incomplete. Request a new reset link.
            </div>
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              Request new link →
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            {error && (
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-red-400" data-testid="reset-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="pw" className="text-sm text-muted-foreground">New password</Label>
              <Input
                id="pw" type="password" value={pw}
                onChange={(e) => setPw(e.target.value)} required autoFocus
                placeholder="At least 8 characters"
                className="bg-card border-border" data-testid="reset-pw-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-sm text-muted-foreground">Confirm password</Label>
              <Input
                id="confirm" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required
                className="bg-card border-border" data-testid="reset-confirm-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
